import { HubConnectionBuilder, IRetryPolicy } from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '../Auth0Provider';
import { environment } from '../environment';

const retryConstantly: IRetryPolicy = { nextRetryDelayInMilliseconds: () => 1000 };

const CLAP_MESSAGE = 'clap';
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

export const useClapStore = (roomName: string | null, onClap: () => void) => {
  const [connected, setConnected] = useState(false);
  const [roomMembers, setRoomMembers] = useState(0);
  const onClapRef = useRef(onClap);
  const peersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const dataChannelsRef = useRef<Map<string, RTCDataChannel>>(new Map());
  const { getTokenSilently } = useAuth0();

  useEffect(() => {
    onClapRef.current = onClap;
  }, [onClap]);

  const hackathonId = roomName?.replace('hackathon-', '') ?? null;

  useEffect(() => {
    if (!hackathonId) return;

    const peers = new Map<string, RTCPeerConnection>();
    const channels = new Map<string, RTCDataChannel>();
    peersRef.current = peers;
    dataChannelsRef.current = channels;

    const connection = new HubConnectionBuilder()
      .withAutomaticReconnect(retryConstantly)
      .withUrl(`${environment.apiUrl}/hubs/clap?hackathonId=${hackathonId}`, {
        accessTokenFactory: () => getTokenSilently(),
      })
      .build();

    const updateMemberCount = () => setRoomMembers(peers.size + 1);

    function setupDataChannel(peerId: string, channel: RTCDataChannel) {
      channels.set(peerId, channel);
      channel.onmessage = ({ data }) => {
        if (data === CLAP_MESSAGE) onClapRef.current();
      };
      channel.onclose = () => {
        channels.delete(peerId);
        updateMemberCount();
      };
      channel.onerror = () => {
        channels.delete(peerId);
        updateMemberCount();
      };
      updateMemberCount();
    }

    function removePeer(peerId: string) {
      peers.get(peerId)?.close();
      peers.delete(peerId);
      channels.delete(peerId);
      updateMemberCount();
    }

    function createPeer(peerId: string): RTCPeerConnection {
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peers.set(peerId, pc);

      pc.onicecandidate = ({ candidate }) => {
        if (candidate) {
          connection.invoke('SendIceCandidate', peerId, candidate.candidate, candidate.sdpMid, candidate.sdpMLineIndex);
        }
      };

      pc.ondatachannel = ({ channel }) => setupDataChannel(peerId, channel);

      pc.onconnectionstatechange = () => {
        if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) removePeer(peerId);
      };

      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete' && pc.iceConnectionState === 'failed') removePeer(peerId);
      };

      return pc;
    }

    async function initiateConnection(peerId: string) {
      const pc = createPeer(peerId);
      const channel = pc.createDataChannel('clap');
      setupDataChannel(peerId, channel);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await connection.invoke('SendOffer', peerId, offer.sdp);
    }

    connection.on('ReceivePeers', async (peerIds: string[]) => {
      await Promise.all(peerIds.map((peerId) => initiateConnection(peerId)));
    });

    connection.on('PeerJoined', () => updateMemberCount());
    connection.on('PeerLeft', removePeer);

    connection.on('ReceiveOffer', async (fromId: string, sdp: string) => {
      try {
        const pc = createPeer(fromId);
        await pc.setRemoteDescription({ type: 'offer', sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await connection.invoke('SendAnswer', fromId, answer.sdp);
      } catch {
        removePeer(fromId);
      }
    });

    connection.on('ReceiveAnswer', async (fromId: string, sdp: string) => {
      try {
        await peers.get(fromId)?.setRemoteDescription({ type: 'answer', sdp });
      } catch {
        removePeer(fromId);
      }
    });

    connection.on('ReceiveIceCandidate', async (fromId: string, candidate: string, sdpMid: string, sdpMLineIndex: number | null) => {
      try {
        await peers.get(fromId)?.addIceCandidate({ candidate, sdpMid, sdpMLineIndex: sdpMLineIndex ?? undefined });
      } catch {
        // Stale or invalid ICE candidate; ignore and let the connection time out naturally
      }
    });

    connection.start().then(() => setConnected(true)).catch(() => setConnected(false));

    return () => {
      peers.forEach((pc) => pc.close());
      connection.stop();
      setConnected(false);
      setRoomMembers(0);
    };
  }, [hackathonId, getTokenSilently]);

  const sendClap = useCallback(() => {
    dataChannelsRef.current.forEach((channel) => {
      if (channel.readyState === 'open') channel.send(CLAP_MESSAGE);
    });
    onClapRef.current();
  }, []);

  return { sendClap, connected, roomMembers };
};
